import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ParameterConfig } from '@/types/mcpTool';
import { Brain, User, AlertCircle } from 'lucide-react';

interface BodyFieldWithOptionsProps {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  value: any;
  config: ParameterConfig;
  onChange: (value: any, config: ParameterConfig) => void;
  placeholder?: string;
  description?: string;
  enumOptions?: string[];
}

/**
 * BodyFieldWithOptions Component
 *
 * Body/Query parameter field with AI/Human handoff options
 * Features:
 * - Direct value input
 * - 3 checkboxes: Required, Let AI Decide, Handoff to Human
 * - Logic: If value entered → disable checkboxes
 * - If no value → must select at least one option
 * - Required can combine with AI or Human
 * - AI and Human are mutually exclusive
 */
export const BodyFieldWithOptions: React.FC<BodyFieldWithOptionsProps> = ({
  name,
  type,
  value,
  config,
  onChange,
  placeholder,
  description,
  enumOptions,
}) => {
  const hasValue = value !== '' && value !== null && value !== undefined;

  const handleValueChange = (newValue: any) => {
    // When user enters a value, disable all checkboxes
    onChange(newValue, {
      ...config,
      value: newValue,
      required: false,
      let_ai_decide: false,
      handoff_to_human: false,
    });
  };

  const handleRequiredChange = (checked: boolean) => {
    onChange(value, {
      ...config,
      required: checked,
    });
  };

  const handleAIDecideChange = (checked: boolean) => {
    onChange(value, {
      ...config,
      let_ai_decide: checked,
      handoff_to_human: checked ? false : config.handoff_to_human, // Uncheck human if AI is checked
    });
  };

  const handleHumanHandoffChange = (checked: boolean) => {
    onChange(value, {
      ...config,
      handoff_to_human: checked,
      let_ai_decide: checked ? false : config.let_ai_decide, // Uncheck AI if human is checked
    });
  };

  const showValidationError = !hasValue && !config.let_ai_decide && !config.handoff_to_human;

  return (
    <div className="space-y-3 p-3 rounded-lg border bg-white">
      {/* Field Header */}
      <div className="flex items-center justify-between">
        <Label htmlFor={`body-${name}`} className="flex items-center gap-2">
          {name}
          {type !== 'string' && (
            <Badge variant="outline" className="text-xs">
              {type}
            </Badge>
          )}
        </Label>
      </div>

      {/* Value Input */}
      <div className="space-y-2">
        {enumOptions ? (
          <select
            id={`body-${name}`}
            value={value || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Select...</option>
            {enumOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : type === 'boolean' ? (
          <select
            id={`body-${name}`}
            value={value?.toString() || ''}
            onChange={(e) => handleValueChange(e.target.value === 'true')}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Select...</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </select>
        ) : type === 'object' || type === 'array' ? (
          <Textarea
            id={`body-${name}`}
            placeholder={placeholder || `Enter JSON for ${name}`}
            value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value || ''}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleValueChange(parsed);
              } catch {
                handleValueChange(e.target.value);
              }
            }}
            rows={4}
            className="font-mono text-sm"
          />
        ) : (
          <Input
            id={`body-${name}`}
            type={type === 'number' ? 'number' : 'text'}
            placeholder={placeholder || `Enter ${name}`}
            value={value || ''}
            onChange={(e) => handleValueChange(type === 'number' ? Number(e.target.value) : e.target.value)}
          />
        )}

        {description && (
          <p className="text-xs text-gray-500">{description}</p>
        )}
      </div>

      {/* Options Checkboxes */}
      <div className="space-y-2 pt-2 border-t">
        <p className="text-xs font-medium text-gray-600">
          {hasValue ? 'Direct value entered (options disabled)' : 'Or choose an option:'}
        </p>

        <div className="space-y-2">
          {/* Required Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`required-${name}`}
              checked={config.required}
              onCheckedChange={handleRequiredChange}
              disabled={hasValue}
            />
            <Label
              htmlFor={`required-${name}`}
              className={`text-sm font-normal flex items-center gap-2 ${hasValue ? 'text-gray-400' : 'cursor-pointer'}`}
            >
              <AlertCircle className="h-4 w-4 text-red-500" />
              Required - Field must have a value
            </Label>
          </div>

          {/* Let AI Decide Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`ai-${name}`}
              checked={config.let_ai_decide}
              onCheckedChange={handleAIDecideChange}
              disabled={hasValue}
            />
            <Label
              htmlFor={`ai-${name}`}
              className={`text-sm font-normal flex items-center gap-2 ${hasValue ? 'text-gray-400' : 'cursor-pointer'}`}
            >
              <Brain className="h-4 w-4 text-purple-500" />
              Let AI Decide - AI agent will determine the value
            </Label>
          </div>

          {/* Handoff to Human Checkbox */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id={`human-${name}`}
              checked={config.handoff_to_human}
              onCheckedChange={handleHumanHandoffChange}
              disabled={hasValue}
            />
            <Label
              htmlFor={`human-${name}`}
              className={`text-sm font-normal flex items-center gap-2 ${hasValue ? 'text-gray-400' : 'cursor-pointer'}`}
            >
              <User className="h-4 w-4 text-blue-500" />
              Handoff to Human - Human agent must provide this value
            </Label>
          </div>
        </div>

        {/* Validation Error */}
        {showValidationError && (
          <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>
              Please either enter a value or select at least one option (Let AI Decide or Handoff to Human)
            </span>
          </div>
        )}

        {/* Selected Options Summary */}
        {!hasValue && (config.required || config.let_ai_decide || config.handoff_to_human) && (
          <div className="flex flex-wrap gap-2 pt-2">
            {config.required && (
              <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                <AlertCircle className="h-3 w-3 mr-1" />
                Required
              </Badge>
            )}
            {config.let_ai_decide && (
              <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                <Brain className="h-3 w-3 mr-1" />
                AI Decides
              </Badge>
            )}
            {config.handoff_to_human && (
              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                <User className="h-3 w-3 mr-1" />
                Human Agent
              </Badge>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
